/**
 * EvaluationPage - Admin-only classification evaluation dashboard
 *
 * Displays classification quality metrics, version comparisons,
 * feedback distribution, recent reviews, and a review queue.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  BarChart3,
  CheckCircle2,
  Clock3,
  ListChecks,
  MessageSquareMore,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Users,
  XCircle,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

import { fetchWithAuth } from '../../../utils/api';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import SegmentedNav from '../../../components/ui/segmented-nav';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../../components/ui/chart';

interface VersionMetrics {
  prompt_version: string | null;
  prompt_version_details: { [dim: string]: string } | null;
  taxonomy_version: string | null;
  model_version: string | null;
  signal_count: number;
  feedback_count: number;
  avg_accuracy_score: number | null;
  avg_total_cost: number | null;
  avg_total_tokens: number | null;
}

interface DashboardData {
  total_classifications: number;
  total_feedback: number;
  avg_accuracy: number | null;
  versions: VersionMetrics[];
}

interface FeedbackItem {
  id: string;
  signal_id: string;
  classification_id: string;
  reviewer_id: string;
  accuracy_score: number | null;
  root_cause_accuracy: string | null;
  label_relevance: string | null;
  strain_accuracy: string | null;
  harm_accuracy: string | null;
  severity_accuracy: string | null;
  consumer_duty_accuracy: string | null;
  overall_helpfulness: string | null;
  comment: string | null;
  taxonomy_version: string | null;
  prompt_version: string | null;
  prompt_version_details: { [dim: string]: string } | null;
  model_version: string | null;
  created_at: string | null;
}

interface ReviewQueueItem {
  classification_id: string;
  signal_id: string;
  prompt_version: string | null;
  prompt_version_details: { [dim: string]: string } | null;
  model_version: string | null;
  conduct_score: number | null;
  created_at: string | null;
}

type EvaluationTab = 'overview' | 'feedback' | 'queue';
type AccuracyKey = 'accurate' | 'partially_accurate' | 'inaccurate';

const DIMENSION_KEYS = [
  { key: 'root_cause_accuracy', label: 'Root Cause' },
  { key: 'label_relevance', label: 'Labels' },
  { key: 'strain_accuracy', label: 'Strain' },
  { key: 'harm_accuracy', label: 'Harm' },
  { key: 'severity_accuracy', label: 'Severity' },
  { key: 'consumer_duty_accuracy', label: 'Consumer Duty' },
] as const;

const ACCURACY_VALUES: AccuracyKey[] = ['accurate', 'partially_accurate', 'inaccurate'];

const CHART_CONFIG: ChartConfig = {
  accurate: { label: 'Accurate', color: 'var(--go-success)' },
  partially_accurate: { label: 'Partially accurate', color: 'var(--go-good-vibes)' },
  inaccurate: { label: 'Inaccurate', color: 'var(--go-bittersweet)' },
};

function VersionCell({
  value,
  details,
}: {
  value: string | null;
  details?: { [dim: string]: string } | null;
}) {
  if (!value) return <span className="text-muted-foreground">-</span>;

  const hasDetails = details && Object.keys(details).length > 0;
  const display = value.length <= 12 ? value : `${value.slice(0, 10)}...`;

  if (!hasDetails) return <span>{display}</span>;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help underline decoration-dotted underline-offset-4">
            {display}
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-0.5 text-xs">
            {Object.entries(details!).map(([dim, ver]) => (
              <div key={dim}>
                <span className="text-muted-foreground">{dim.replace(/_/g, ' ')}:</span>{' '}
                <span className="font-medium">{ver.startsWith('v') ? ver : `v${ver}`}</span>
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function formatPercent(value: number | null): string {
  if (value == null) return '-';
  return `${(value * 100).toFixed(0)}%`;
}

function formatCurrency(value: number | null): string {
  if (value == null) return '-';
  return `$${value.toFixed(4)}`;
}

function getAccuracyBadgeVariant(score: number | null) {
  if (score == null) return 'secondary' as const;
  if (score >= 0.8) return 'success' as const;
  if (score >= 0.5) return 'attention' as const;
  return 'destructive' as const;
}

function getConductScoreVariant(score: number | null) {
  if (score == null) return 'secondary' as const;
  if (score >= 8) return 'destructive' as const;
  if (score >= 4) return 'attention' as const;
  return 'success' as const;
}

function getConductScoreLabel(score: number | null) {
  if (score == null) return '-';
  return score.toFixed(1);
}

function MetricCard({
  label,
  value,
  subtext,
  icon,
  tone,
}: {
  label: string;
  value: string;
  subtext: string;
  icon: React.ReactNode;
  tone: 'primary' | 'success' | 'attention';
}) {
  const toneClasses = {
    primary: 'border-primary/15 bg-accent text-primary',
    success: 'border-[var(--go-success)]/20 bg-[color:rgba(22,163,74,0.08)] text-[var(--go-success)]',
    attention: 'border-[var(--go-bittersweet)]/20 bg-[color:rgba(255,116,97,0.08)] text-[var(--go-bittersweet)]',
  }[tone];

  return (
    <Card className="rounded-xl">
      <CardContent className="flex items-start justify-between gap-4 p-5">
        <div className="min-w-0 space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            {label}
          </p>
          <div className="font-jakarta text-3xl font-extrabold tracking-[-0.04em] text-foreground">
            {value}
          </div>
          <p className="text-[13px] text-muted-foreground">{subtext}</p>
        </div>
        <div className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border', toneClasses)}>
          {icon}
        </div>
      </CardContent>
    </Card>
  );
}

function SectionCard({
  title,
  description,
  action,
  children,
  className,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Card className={cn('rounded-xl', className)}>
      <CardHeader className="border-b border-border pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle>{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          {action}
        </div>
      </CardHeader>
      <CardContent className="p-0">{children}</CardContent>
    </Card>
  );
}

export default function EvaluationPage() {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [feedbackTotal, setFeedbackTotal] = useState(0);
  const [reviewQueue, setReviewQueue] = useState<ReviewQueueItem[]>([]);
  const [reviewQueueTotal, setReviewQueueTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<EvaluationTab>('overview');
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [dashData, fbData, queueData] = await Promise.all([
        fetchWithAuth('/v1/evaluation/dashboard?days=30'),
        fetchWithAuth('/v1/evaluation/feedback?page=1&page_size=20'),
        fetchWithAuth('/v1/evaluation/review-queue?limit=50'),
      ]);

      setDashboard(dashData);
      setFeedback(fbData.items || []);
      setFeedbackTotal(fbData.total || 0);
      setReviewQueue(queueData.items || []);
      setReviewQueueTotal(queueData.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const distributionData = useMemo(() => {
    return DIMENSION_KEYS.map(({ key, label }) => {
      const counts: Record<AccuracyKey, number> = {
        accurate: 0,
        partially_accurate: 0,
        inaccurate: 0,
      };

      for (const fb of feedback) {
        const value = fb[key as keyof FeedbackItem] as string | null;
        if (value && value in counts) {
          counts[value as AccuracyKey] += 1;
        }
      }

      return { dimension: label, ...counts };
    });
  }, [feedback]);

  const sortedVersions = useMemo(
    () => [...(dashboard?.versions || [])].sort((a, b) => b.signal_count - a.signal_count),
    [dashboard?.versions]
  );

  const sectionNav = [
    {
      value: 'overview',
      label: 'Version overview',
      icon: BarChart3,
    },
    {
      value: 'feedback',
      label: 'Recent feedback',
      icon: MessageSquareMore,
    },
    {
      value: 'queue',
      label: 'Review queue',
      icon: ListChecks,
    },
  ] as const;

  if (loading) {
    return (
      <div className="flex h-full flex-col overflow-auto bg-[var(--go-spring)] px-4 py-4 md:px-6 md:py-6 xl:px-8 xl:py-8">
        <div className="flex w-full flex-col gap-6">
          <div className="space-y-4">
            <div className="h-4 w-28 rounded-full bg-muted/70" />
            <div className="h-10 w-72 rounded-lg bg-muted/70" />
            <div className="h-5 w-[28rem] max-w-full rounded-lg bg-muted/60" />
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {[1, 2, 3].map((index) => (
              <div key={index} className="h-28 rounded-xl border border-border bg-background/80" />
            ))}
          </div>
          <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
            <div className="h-[28rem] rounded-xl border border-border bg-background/80" />
            <div className="h-[28rem] rounded-xl border border-border bg-background/80" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center overflow-auto bg-[var(--go-spring)] px-4 py-6 md:px-6 md:py-8">
        <Card className="w-full max-w-xl rounded-xl border-destructive/20 bg-background/90">
          <CardHeader className="border-b border-border pb-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-destructive/20 bg-[color:rgba(255,116,97,0.08)] text-[var(--go-bittersweet)]">
                <AlertCircle className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <CardTitle>Failed to load evaluation data</CardTitle>
                <CardDescription>{error}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-end">
            <Button variant="outline" onClick={loadData} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!dashboard) return null;

  return (
    <div className="flex h-full flex-col overflow-auto bg-[var(--go-spring)] px-4 py-4 md:px-6 md:py-6 xl:px-8 xl:py-8">
      <div className="flex w-full flex-col gap-6">
        <header className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="max-w-3xl space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="border-primary/15 bg-accent text-primary">
                Admin view
              </Badge>
              <Badge variant="secondary">
                <Clock3 className="h-3.5 w-3.5" />
                Last 30 days
              </Badge>
            </div>
            <div className="space-y-2">
              <h1 className="font-jakarta text-3xl font-extrabold tracking-[-0.03em] text-foreground md:text-4xl">
                Classification Evaluation
              </h1>
              <p className="max-w-2xl text-[13px] leading-[1.7] text-muted-foreground md:text-sm">
                Monitor classifier quality, compare prompt and taxonomy versions, and review feedback from the admin shell.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary">
              <Users className="h-3.5 w-3.5" />
              {reviewQueueTotal.toLocaleString()} queued reviews
            </Badge>
            <Button variant="outline" size="sm" onClick={loadData} disabled={loading} className="gap-2">
              <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
              Refresh
            </Button>
          </div>
        </header>

        <section className="grid gap-4 md:grid-cols-3">
          <MetricCard
            label="Classifications"
            value={dashboard.total_classifications.toLocaleString()}
            subtext="Evaluated in the last 30 days"
            icon={<BarChart3 className="h-5 w-5" />}
            tone="primary"
          />
          <MetricCard
            label="Reviews submitted"
            value={dashboard.total_feedback.toLocaleString()}
            subtext={`${reviewQueueTotal.toLocaleString()} still in the review queue`}
            icon={<ShieldCheck className="h-5 w-5" />}
            tone="success"
          />
          <MetricCard
            label="Average accuracy"
            value={formatPercent(dashboard.avg_accuracy)}
            subtext="Rolling feedback across all dimensions"
            icon={<Sparkles className="h-5 w-5" />}
            tone="attention"
          />
        </section>

        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <SegmentedNav
            ariaLabel="Evaluation sections"
            activeKey={activeTab}
            items={sectionNav.map((item) => {
              const Icon = item.icon;

              return {
                key: item.value,
                label: item.label,
                onSelect: () => setActiveTab(item.value),
                icon: <Icon className="h-4 w-4" />,
              };
            })}
          />

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock3 className="h-3.5 w-3.5" />
            Data is loaded directly from the evaluation API.
          </div>
        </div>

        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
              <SectionCard
                title="Feedback distribution"
                description="How reviewers scored each classification dimension."
                action={<Badge variant="secondary">Stacked by accuracy</Badge>}
              >
                {feedback.length > 0 ? (
                  <div className="px-4 py-4">
                    <ChartContainer config={CHART_CONFIG} className="h-[360px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={distributionData} layout="vertical" barCategoryGap={14}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                          <XAxis type="number" allowDecimals={false} tickLine={false} axisLine={false} />
                          <YAxis
                            type="category"
                            dataKey="dimension"
                            width={108}
                            tickLine={false}
                            axisLine={false}
                            tick={{ fontSize: 12 }}
                          />
                          <ChartTooltip content={<ChartTooltipContent />} />
                          <Bar dataKey="accurate" stackId="a" fill={CHART_CONFIG.accurate.color} radius={[0, 0, 0, 0]} />
                          <Bar dataKey="partially_accurate" stackId="a" fill={CHART_CONFIG.partially_accurate.color} radius={[0, 0, 0, 0]} />
                          <Bar dataKey="inaccurate" stackId="a" fill={CHART_CONFIG.inaccurate.color} radius={[0, 6, 6, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </ChartContainer>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {ACCURACY_VALUES.map((key) => (
                        <Badge key={key} variant="outline" className="capitalize">
                          <span
                            className="h-2.5 w-2.5 rounded-full"
                            style={{ backgroundColor: CHART_CONFIG[key].color }}
                          />
                          {CHART_CONFIG[key].label}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex min-h-[18rem] items-center justify-center px-6 py-8 text-center">
                    <div className="max-w-sm space-y-2">
                      <p className="text-sm font-semibold text-foreground">No feedback yet</p>
                      <p className="text-[13px] leading-[1.7] text-muted-foreground">
                        Once reviewers start submitting evaluations, the dimension breakdown will appear here.
                      </p>
                    </div>
                  </div>
                )}
              </SectionCard>

              <SectionCard
                title="Version comparison"
                description="Prompt, taxonomy, and model combinations from the current window."
                action={<Badge variant="secondary">{sortedVersions.length} versions</Badge>}
              >
                <div className="max-h-[360px] overflow-auto">
                  <Table className="text-[13px]">
                    <TableHeader className="sticky top-0 z-10 bg-background">
                      <TableRow className="border-border bg-background/95">
                        <TableHead className="pl-4">Prompt</TableHead>
                        <TableHead>Taxonomy</TableHead>
                        <TableHead>Model</TableHead>
                        <TableHead className="text-right">Signals</TableHead>
                        <TableHead className="text-right">Reviews</TableHead>
                        <TableHead className="text-right">Accuracy</TableHead>
                        <TableHead className="text-right">Cost</TableHead>
                        <TableHead className="pr-4 text-right">Tokens</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedVersions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="px-4 py-10 text-center text-muted-foreground">
                            No classification versions found in the last 30 days.
                          </TableCell>
                        </TableRow>
                      ) : (
                        sortedVersions.map((version, index) => (
                          <TableRow key={`${version.prompt_version ?? 'prompt'}-${index}`} className="hover:bg-muted/40">
                            <TableCell className="pl-4 font-mono text-xs">
                              <VersionCell value={version.prompt_version} details={version.prompt_version_details} />
                            </TableCell>
                            <TableCell>{version.taxonomy_version || '-'}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{version.model_version || '-'}</TableCell>
                            <TableCell className="text-right">{version.signal_count}</TableCell>
                            <TableCell className="text-right">{version.feedback_count}</TableCell>
                            <TableCell className="text-right">
                              <Badge variant={getAccuracyBadgeVariant(version.avg_accuracy_score)}>
                                {formatPercent(version.avg_accuracy_score)}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs">
                              {formatCurrency(version.avg_total_cost)}
                            </TableCell>
                            <TableCell className="pr-4 text-right">
                              {version.avg_total_tokens?.toLocaleString() || '-'}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </SectionCard>
            </div>
          </div>
        )}

        {activeTab === 'feedback' && (
          <div className="space-y-6">
            <SectionCard
              title="Recent feedback"
              description="Reviewer responses and notes from the latest classification reviews."
              action={<Badge variant="secondary">{feedbackTotal.toLocaleString()} total</Badge>}
            >
              <div className="max-h-[calc(100vh-22rem)] overflow-auto">
                <Table className="text-[13px]">
                  <TableHeader className="sticky top-0 z-10 bg-background">
                    <TableRow className="border-border bg-background/95">
                      <TableHead className="pl-4">Date</TableHead>
                      <TableHead>Signal</TableHead>
                      <TableHead className="text-right">Accuracy</TableHead>
                      <TableHead>Helpfulness</TableHead>
                      <TableHead>Comment</TableHead>
                      <TableHead className="pr-4">Version</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {feedback.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                          No feedback submitted yet. Use the review flow on a record to get started.
                        </TableCell>
                      </TableRow>
                    ) : (
                      feedback.map((item) => (
                        <TableRow key={item.id} className="hover:bg-muted/40">
                          <TableCell className="pl-4 whitespace-nowrap text-xs text-muted-foreground">
                            {item.created_at ? new Date(item.created_at).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {item.signal_id.slice(0, 8)}...
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant={getAccuracyBadgeVariant(item.accuracy_score)}>
                              {formatPercent(item.accuracy_score)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs">
                            {item.overall_helpfulness === 'helpful' && (
                              <Badge variant="success">
                                <CheckCircle2 className="h-3 w-3" />
                                Helpful
                              </Badge>
                            )}
                            {item.overall_helpfulness === 'not_helpful' && (
                              <Badge variant="destructive">
                                <XCircle className="h-3 w-3" />
                                Not helpful
                              </Badge>
                            )}
                            {!item.overall_helpfulness && <span className="text-muted-foreground">-</span>}
                          </TableCell>
                          <TableCell className="max-w-[18rem] truncate text-xs text-muted-foreground">
                            {item.comment || '-'}
                          </TableCell>
                          <TableCell className="pr-4 font-mono text-xs">
                            <VersionCell value={item.prompt_version} details={item.prompt_version_details} />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </SectionCard>
          </div>
        )}

        {activeTab === 'queue' && (
          <div className="space-y-6">
            <SectionCard
              title="Review queue"
              description="Classifications that still need a manual quality check."
              action={<Badge variant="secondary">{reviewQueueTotal.toLocaleString()} pending</Badge>}
            >
              <div className="max-h-[calc(100vh-22rem)] overflow-auto">
                <Table className="text-[13px]">
                  <TableHeader className="sticky top-0 z-10 bg-background">
                    <TableRow className="border-border bg-background/95">
                      <TableHead className="pl-4">Classified</TableHead>
                      <TableHead>Signal</TableHead>
                      <TableHead className="text-right">Conduct score</TableHead>
                      <TableHead>Prompt version</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead className="pr-4">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reviewQueue.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                          All classifications have been reviewed.
                        </TableCell>
                      </TableRow>
                    ) : (
                      reviewQueue.map((item) => (
                        <TableRow
                          key={item.classification_id}
                          className="cursor-pointer hover:bg-muted/40"
                          onClick={() => navigate(`/v4/records/all/${item.signal_id}`)}
                        >
                          <TableCell className="pl-4 whitespace-nowrap text-xs text-muted-foreground">
                            {item.created_at ? new Date(item.created_at).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {item.signal_id.slice(0, 8)}...
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant={getConductScoreVariant(item.conduct_score)}>
                              {getConductScoreLabel(item.conduct_score)}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            <VersionCell value={item.prompt_version} details={item.prompt_version_details} />
                          </TableCell>
                          <TableCell className="text-xs">{item.model_version || '-'}</TableCell>
                          <TableCell className="pr-4 text-xs text-muted-foreground">
                            View record
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </SectionCard>
          </div>
        )}
      </div>
    </div>
  );
}
